import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';

type WStatus = { configured: boolean; state: string; instance: string; profileName?: string | null; ownerJid?: string | null; message?: string };

export default function WhatsAppPage() {
  const [status, setStatus] = useState<WStatus | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<WStatus>('/messages/whatsapp/status');
      setStatus(r);
      if (r.state === 'open') setQr(null);
    } catch {
      setStatus(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function onQr() {
    setBusy(true);
    try {
      const r = await api.get<{ base64: string; code: string | null }>('/messages/whatsapp/qr');
      setQr(r.base64);
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Falha ao gerar QR';
      if (msg.includes('já conectada')) { toast.success('Já está conectada.'); setQr(null); }
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    setBusy(true);
    try {
      await api.post('/messages/whatsapp/logout');
      toast.success('Desconectado.');
      setQr(null);
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao desconectar');
    } finally {
      setBusy(false);
    }
  }

  const stateLabel: Record<string, string> = { open: 'Conectado', connecting: 'Conectando...', close: 'Desconectado', desconhecido: 'Sem instância' };

  return (
    <div className="max-w-xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Envios</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">WhatsApp (Evolution)</h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">
          Instância dedicada: <code>{status?.instance ?? 'claro-empresas-mvp'}</code>
        </p>
      </header>
      <div className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">{status ? (stateLabel[status.state] ?? status.state) : 'Carregando...'}</p>
            {status?.ownerJid && <p className="text-xs text-[var(--text-dim)]">{status.ownerJid.split('@')[0]}</p>}
          </div>
          <div className="flex gap-2">
            {status?.state !== 'open' && (
              <button onClick={onQr} disabled={busy} className="h-9 rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Conectar (QR)</button>
            )}
            {status?.state === 'open' && (
              <button onClick={onLogout} disabled={busy} className="h-9 rounded-lg border border-[var(--border-strong)] px-4 text-sm font-medium hover:bg-[var(--bg-subtle)]">Desconectar</button>
            )}
          </div>
        </div>
        {status && !status.configured && (
          <p className="mt-3 text-xs text-[var(--text-dim)]">
            {status.message} Configure EVOLUTION_URL/EVOLUTION_API_KEY no .env do server (instância {status.instance}).
          </p>
        )}
        {qr && (
          <div className="mt-4 text-center">
            <img src={qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`} alt="QR code de conexão do WhatsApp" className="mx-auto w-56 rounded-lg border border-[var(--border)]" />
            <p className="mt-2 text-xs text-[var(--text-dim)]">Escaneie com o WhatsApp do MVP. A tela atualiza o status sozinha quando conectar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
